import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import Breadcrumb from "@/components/Breadcrumb";
import PageHeader from "@/components/PageHeader";
import { services } from "@/data/services";
import { Button, cx } from "@/components/kit";

const ease = [0.22, 1, 0.36, 1];

const DienstenPage = () => {
  useSeo({
    title: "Diensten | AI Marketing, SEO, Webdesign & Content",
    description:
      "Bekijk alle diensten van UPPIT: AI marketing, SEO optimalisatie, webdesign AI, Webflow & WordPress, social media advertising, content optimalisatie en AI-optimalisatie (GEO). Voor MKB & ZZP.",
    path: "/diensten",
    ogSlug: "page/diensten",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: services.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: s.title,
        url: `https://uppit.nl/diensten/${s.slug}`,
      })),
    },
  });

  return (
    <article data-testid="diensten-page" className="relative pt-28 lg:pt-32 pb-24 bg-ivory">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Diensten", href: "/diensten" }]} />
        <PageHeader
          kicker="Diensten · Overzicht"
          italic="Zeven groeimotoren."
          bold="Eén partner."
          lede="Van strategie tot uitvoering. UPPIT combineert AI-snelheid met menselijke expertise — zodat MKB en ZZP'ers structureel groeien: meer leads, hogere conversie en betere vindbaarheid."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.06, ease }}
              >
                <Link
                  to={`/diensten/${s.slug}`}
                  data-testid={`diensten-card-${s.slug}`}
                  className="group relative overflow-hidden flex h-full flex-col justify-between rounded-[1.5rem] border border-ivory-400 bg-white p-7 lg:p-8 shadow-card-light hover:border-electric/40 hover:shadow-card-lift hover:-translate-y-1 transition-all duration-300"
                >
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -right-6 -bottom-8 w-36 h-36 lg:w-44 lg:h-44 transition-transform duration-500 ease-out group-hover:scale-110 group-hover:-rotate-3"
                  >
                    <img src={`/v2/obj-${s.slug}.png`} alt="" loading="lazy" className="w-full h-full object-contain drop-shadow-[0_20px_32px_rgba(0,56,255,0.16)]" />
                  </div>
                  <div className="relative">
                    <div className="flex items-center justify-between mb-6">
                      <div className="w-12 h-12 rounded-xl bg-electric/10 border border-electric/15 grid place-items-center text-electric group-hover:bg-electric group-hover:text-white transition-colors duration-300">
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="font-mono text-[11px] tracking-[0.25em] text-coal/35">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h2 className="font-display font-bold text-2xl text-coal leading-tight mb-3">{s.title}</h2>
                    <p className="text-[15px] text-coal/60 leading-relaxed max-w-[15rem]">{s.short}</p>
                  </div>
                  <div className="relative mt-7 inline-flex items-center gap-2 text-sm font-semibold text-coal group-hover:text-electric transition-colors">
                    <span className="link-draw">Bekijk dienst</span>
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-16 lg:mt-20 rounded-[1.75rem] bg-obsidian text-ivory p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center overflow-hidden relative">
          <div className="glow-orb w-[400px] h-[400px] bg-electric/25 -top-20 right-0" />
          <div className="relative lg:col-span-8">
            <h2 className={cx("font-display font-black tracking-tight leading-[0.95] text-3xl lg:text-4xl")}>
              Niet zeker welke dienst voor jou is? <span className="text-gradient-blue">Wij denken mee.</span>
            </h2>
            <p className="mt-4 text-white/60 max-w-xl">
              Doe de gratis AI Growth Scan of plan een gesprek — binnen 30 minuten weet je waar je groeikansen liggen.
            </p>
          </div>
          <div className="relative lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
            <Button to="/ai-scan" variant="primary" testid="diensten-cta-aiscan" className="w-full justify-center">
              Start AI Growth Scan
            </Button>
            <Button to="/contact" variant="outlineLight" testid="diensten-cta-contact" className="w-full justify-center">
              Direct contact
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
};

export default DienstenPage;
